<template>
  <div>
    <v-form ref="form" :disabled="disableForm">
      <v-container
        style="border: solid 1px #e1e3e2; border-radius: 15px; padding: 15px"
      >
        <h3 style="font-weight: bold" class="d-flex justify-center mb-5">
          {{ $t('descriptions.patient_information') }}
        </h3>
        <v-row dense>
          <v-col class="d-flex" cols="12" sm="4">
            <v-radio-group
              v-model="resource.kind_of_donor_id"
              :rules="formRules"
              :disabled="disableFlags"
              data-cy='patient-situation'
            >
              <p>{{$t('descriptions.patient_situation')}}:<span style="color: red">*</span></p>

              <v-radio
                data-cy="patient-me"
                :label="$t('descriptions.me')"
                value="1"
                color="#086318"
                :disabled="isMEDisabled"
              />

              <v-radio
                data-cy="patient-pcr"
                :label="$t('descriptions.pcr')"
                value="2"
                color="#086318"
                :disabled="isPCRDisabled"
                @change="identified = 'true'"
              />
            </v-radio-group>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            v-if="resource.kind_of_donor_id"
          >
            <v-radio-group
              v-model="identified"
              :disabled="resource.kind_of_donor_id == '2'"
              :rules="formRules"
              data-test='patient_identified'
            >
              <p>{{$t('descriptions.identified')}}<span style="color: red">*</span></p>

              <v-radio
                :label="$t('buttons.yes')"
                value="true"
                color="#086318"
                @click="changeName()"
              ></v-radio>

              <v-radio :label="$t('buttons.no')" value="false" color="#086318"></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row dense v-if="resource.kind_of_donor_id">
          <v-col class="d-flex" cols="12" sm="6" md="4" v-if="identified">
            <v-text-field
              color="#256936"
              class="rounded-lg"
              outlined
              v-model="resource.medical_records"
              dense
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.medical_records') }}
              </template>
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            sm="6"
            md="5"
            v-if="identified == 'true'"
          >
            <v-text-field
              data-cy="patient-name"
              color="#256936"
              :rules="nameRules"
              outlined
              dense
              v-model="resource.name"
              class="rounded-lg"
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.full_name') }} <span style="color: red">*</span>
              </template>
            </v-text-field>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            md="3"
            v-if="identified == 'true'"
          >
            <v-text-field
              data-cy="patient-cpf"
              color="#256936"
              outlined
              v-model="resource.cpf"
              class="rounded-lg"
              :rules="formRules"
              maxlength="14"
              v-mask="'###.###.###-##'"
              dense
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.cpf') }} <span style="color: red">*</span>
              </template>
            </v-text-field>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            md="2"
            v-if="identified == 'true'"
          >
            <v-text-field
              color="#256936"
              :label="$t('inputs.patient_data.rg')"
              class="rounded-lg"
              outlined
              v-model="resource.rg"
              maxlength="13"
              v-mask="'##.###.###-##'"
              dense
            ></v-text-field>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            md="2"
            v-if="identified == 'true'"
          >
            <v-select
              :items="genders"
              color="#086318"
              v-model="resource.gender_id"
              class="rounded-lg"
              item-value="id"
              item-text="description"
              dense
              outlined
              :label="$t('inputs.patient_data.gender')"
            >
            </v-select>
          </v-col>

          <v-col class="d-flex" cols="12" sm="2" md="2" v-if="identified">
            <v-select
              :items="sex"
              color="#086318"
              v-model="resource.sex_id"
              class="rounded-lg"
              :rules="formRules"
              item-value="id"
              item-text="name"
              dense
              outlined
              data-cy="sex"
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.sex') }} <span style="color: red">*</span>
              </template>
            </v-select>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            :sm="identified == 'true' ? '3' : '4'"
            md="2"
            v-if="identified"
          >
            <v-select
              data-cy="patient-breed"
              :items="breeds"
              color="#086318"
              v-model="resource.breed_id"
              class="rounded-lg"
              item-value="id"
              item-text="description"
              :rules="formRules"
              dense
              outlined
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.breed') }} <span style="color: red">*</span>
              </template>
            </v-select>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            md="2"
            v-if="identified == 'false'"
          >
            <v-text-field
              color="#086318"
              class="rounded-lg"
              :rules=" identified == 'false' "
              type="number"
              outlined
              v-model="resource.idade_provavel"
              dense
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.probable_age') }} <span style="color: red">*</span>
              </template>
            </v-text-field>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            md="2"
            v-if="identified == 'true'"
          >
            <v-text-field
              data-cy="patient-age"
              color="#086318"
              class="rounded-lg"
              :rules="formRules"
              type="date"
              @input="onlyDateInput('resource' ,'pcr_date' , $event)"
              :max="maxDate"
              outlined
              v-model="resource.birth_date"
              dense
              >
              <template v-slot:label>
                {{ $t('inputs.patient_data.birth_date') }} <span style="color: red">*</span>
              </template>
            </v-text-field>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="3"
            md="2"
            v-if="identified == 'true'"
          >
            <v-text-field
              color="#086318"
              class="rounded-lg"
              :label="$t('inputs.patient_data.sus_card')"
              :placeholder="$t('inputs.patient_data.sus_card')"
              maxlength="18"
              v-mask="'###.####.####.####'"
              outlined
              v-model="resource.sus_card"
              dense
            ></v-text-field>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            :sm="identified == 'true' ? '4' : '8'"
            :md="identified == 'true' ? '5' : '2'"
            v-if="identified"
          >
            <v-autocomplete
              color="#256936"
              :label="$t('inputs.patient_data.initial_diagnosis')"
              class="rounded-lg"
              outlined
              :items="diagnosis"
              item-value="id"
              item-text="description"
              v-model="resource.initial_diagnosis_id"
              dense
            ></v-autocomplete>
          </v-col>
 
          <v-col
            class="d-flex"
            cols="12"
            sm="5"
            md="5"
            v-if="otherOptions && otherOptions.description == 'OUTRAS CAUSAS'"
          >
            <v-text-field
              color="#086318"
              class="rounded-lg"
              :rules="formRules"  
              outlined
              v-model="resource.initial_diagnosis"
              dense>
              <label slot="label">
                {{ $t('inputs.patient_data.other_diagnosis') }} <span style="color: red">*</span>
              </label>
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            sm="3"
            md="2"
            v-if="identified == 'true'"
          >
            <v-select
              color="#086318"
              class="rounded-lg"
              v-model="resource.country"
              :items="countries"
              item-text="nome"
              item-value="nome"
              dense
              outlined
              :label="$t('inputs.patient_data.nacionality')"
            >
            </v-select>
          </v-col>
          <v-col class="d-flex" cols="12" sm="5" v-if="identified == 'true'">
            <v-text-field
              data-cy="patient-mother"
              color="#256936"
              :rules="formRules"
              class="rounded-lg"
              outlined
              v-model="resource.mother_name"
              dense
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.mother_name') }} <span style="color: red">*</span>
              </template>
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            sm="4"
            md="3"
            v-if="identified == 'true'"
          >
            <v-text-field
              color="#086318"
              :label="$t('inputs.patient_data.telephone')"
              :placeholder="$t('inputs.patient_data.telephone')"
              outlined
              v-mask="'(##)#####-####'"
              maxlength="14"
              v-model="resource.telephone"
              class="rounded-lg"
              dense
            ></v-text-field>
          </v-col>
          <v-col class="d-flex" cols="12" sm="8" :md="resource.initial_diagnosis_id == 22 ? 4 : 3" v-if="identified">
            <v-autocomplete
              data-cy="hospital-notification"
              :items="hospital"
              :disabled="!showAllUnities"
              color="#086318"
              class="rounded-lg"
              v-model="resource.hospital_id"
              :rules="formRules"
              @change="changeCnes()"
              item-value="id"
              item-text="hospital"
              dense
              outlined
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.hospital') }} <span style="color: red">*</span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            :sm="identified == 'true' ? '3' : '4'"
            md="2"
            v-if="identified"
          >
            <v-text-field
              class="rounded-lg"
              color="#086318"
              :placeholder="$t('inputs.patient_data.cnes')"
              :label="$t('inputs.patient_data.cnes')"
              disabled
              outlined
              dense
              v-model="resource.cnes"
              maxlength="14"
              v-mask="'##############'"
            >
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            :md="identified == 'true' ? '2' : '3'"
            cols="12"
            sm="5"
            v-if="identified"
          >
            <v-select
              data-cy="sector-informant"
              :items="inpatient_sectors"
              color="#086318"
              class="rounded-lg"
              v-model="resource.inpatient_sector_id"
              :rules="formRules"
              item-value="id"
              item-text="description"
              dense
              outlined
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.sector') }} <span style="color: red">*</span>
              </template>
            </v-select>
          </v-col>
          <v-col class="d-flex" cols="12" sm="4" md="2" v-if="identified">
            <v-text-field
              class="rounded-lg"
              color="#086318" 
              outlined
              dense
              v-model="resource.phone_sector_extension"
              maxlength="14"
              v-mask="'(##) ####-####'"
            >
              <template v-slot:label>
                {{ $t('inputs.patient_data.extension') }}
              </template>
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            :sm="identified == 'true' ? '4' : '3'"
            :md="identified == 'true' ? '3' : '2'"
            v-if="identified && resource.kind_of_donor_id == '1'"
          >
            <v-text-field
              disabled
              :label="$t('inputs.patient_data.opo')"
              color="#086318"
              class="rounded-lg"
              v-model="opo_reference"
              dense
              outlined
            >
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            :sm="identified == 'true' ? '4' : '3'"
            :md="identified == 'true' ? '3' : '2'"
            v-if="identified && resource.kind_of_donor_id == '2'"
          >
            <v-text-field
              disabled
              :label="$t('inputs.patient_data.opc')"
              color="#086318"
              class="rounded-lg"
              v-model="opc_reference"
              dense
              outlined
            >
            </v-text-field>
          </v-col>
          <v-col
            class="d-flex"
            cols="12"
            sm="2"
            v-if="canShowRGCT"
          > 
            <v-text-field
              color="#086318"
              placeholder="Apenas Números"
              :label="$t('inputs.patient_data.rgct')"
              outlined
              maxlength="11"
              v-mask="'######-####'"
              v-model="resource.rgct"
              class="rounded-lg"
              dense
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row dense v-if="identified == 'true'">
          <v-col class="d-flex" cols="12" sm="12">
            <p style="font-weight: bold">{{$t('descriptions.responsibles')}}</p>
          </v-col>

          <v-col class="d-flex" cols="12" sm="7">
            <v-text-field
              color="#086318"
              :label="$t('inputs.patient_data.responsible_name')"
              placeholder="Nome completo"
              class="rounded-lg"
              outlined
              v-model="resource.responsible"
              dense
            >
            </v-text-field>
          </v-col>
          <v-col class="d-flex" cols="12" sm="5">
            <v-select
              color="#086318"
              :items="kinships"
              v-model="resource.kinship_id"
              class="rounded-lg"
              item-text="name"
              item-value="id"
              dense
              outlined
              :label="$t('inputs.patient_data.kinship_type')"
              @change="handleKinshipChange"
            >
            </v-select>
          </v-col>
        </v-row>
        <v-row dense v-if="identified == 'true'">
          <v-col class="d-flex" cols="12" sm="12">
            <p style="font-weight: bold">{{ $t('descriptions.address') }}:</p>
          </v-col>
          <v-col class="d-flex" cols="12" sm="3" md="2" v-if="!hasCEP">
            <v-text-field
              color="#086318"
              class="rounded-lg"
              :label="$t('inputs.patient_data.cep')"
              v-mask="'########'"
              placeholder=""
              outlined
              v-model="resource.zip_code"
              dense
            >
            </v-text-field>
          </v-col>
          <v-col class="d-flex" cols="12" sm="2" md="2" v-if="!hasCEP">
            <v-tooltip top color="#41916b">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1"
                  small
                  fab
                  dark
                  color="#41916b"
                  v-bind="attrs"
                  v-on="on"
                  :loading="cepBTN"
                  @click="getAddress()"
                >
                  <v-icon dark>mdi-magnify</v-icon>
                </v-btn>
              </template>
              <span style="color: white">{{$t('buttons.search_cep')}}</span>
            </v-tooltip>
          </v-col>
          <v-col class="ml-auto d-flex justify-end" cols="12" sm="2" md="2">
            <v-checkbox
              v-model="hasCEP"
              :label="$t('descriptions.dont_know_cep')"
              color="#41916b"
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row dense v-if="identified == 'true'">
          <v-col class="d-flex" cols="12" sm="4" md="2">
            <v-autocomplete
              color="#086318"
              class="rounded-lg"
              v-model="resource.state"
              :items="state"
              item-text="nome"
              item-value="nome"
              dense
              outlined
              @change="getCities()"
              :label="$t('inputs.patient_data.state')"
            >
            </v-autocomplete>
          </v-col>
          <v-col class="d-flex" cols="12" sm="4" md="2">
            <v-autocomplete
              v-if="!openTextCity"
              color="#086318"
              class="rounded-lg"
              :items="cities"
              v-model="resource.municipality"
              item-text="nome"
              item-value="nome"
              dense
              outlined
              :label="$t('inputs.patient_data.city')"
            >
            </v-autocomplete>
            <v-text-field
              v-if="openTextCity"
              color="#086318"
              class="rounded-lg"
              items="county"
              v-model="resource.municipality"
              item-text="name"
              item-value="id"
              dense
              outlined
              :label="$t('inputs.patient_data.city')"
            >
            </v-text-field>
          </v-col>
          <v-col class="d-flex" cols="12" sm="3" md="2">
            <v-text-field
              color="#086318"
              class="rounded-lg"
              :label="$t('inputs.patient_data.district')"
              :placeholder="$t('inputs.patient_data.district')"
              outlined
              v-model="resource.district"
              dense
            ></v-text-field>
          </v-col>
          <v-col class="d-flex" cols="12" sm="1" md="1">
            <v-text-field
              color="#086318"
              class="rounded-lg"
              :label="$t('inputs.patient_data.number')"
              placeholder=""
              outlined
              type="number"
              v-model="resource.number"
              dense
            ></v-text-field>
          </v-col>
          <v-col class="d-flex" cols="12" sm="12" md="5">
            <v-text-field
              class="rounded-lg"
              color="#086318"
              :label="$t('inputs.patient_data.address')"
              placeholder="Ex: Rua X, Loteamento Y..."
              outlined
              v-model="resource.address"
              dense
            ></v-text-field>
          </v-col>

          <v-col class="d-flex" cols="12" sm="12">
            <v-text-field
              class="rounded-lg"
              color="#086318"
              :label="$t('inputs.patient_data.complement')"
              placeholder="Ex: Próximo ao Mercadinho..."
              outlined
              v-model="resource.complement"
              dense
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="identified" class="d-flex justify-end">
          <v-btn
            data-cy="btn-save-patient"
            class="rounded-lg mb-3 mr-2"
            elevation="0"
            color="#41916b"
            :disabled="disableForm"
            @click="createDoador()"
          >
            <span class="textBtn" style="color: white">{{
              resource.id ? $t('buttons.to_update') : $t('buttons.save')
            }}</span>
          </v-btn>
          <v-btn
            class="rounded-lg mb-3 mr-4"
            text
            outlined
            color="#41916b"
            @click="$router.push('/pacientes')"
          >
            <v-icon>mdi-arrow-left-bold</v-icon>
          </v-btn>
        </v-row>
      </v-container>
    </v-form>

    <v-dialog
      v-model="dialog"
      transition="dialog-bottom-transition"
      max-width="500px"
      class="rounded-lg"
      persistent
    >
      <v-card>
        <v-card-title>
          <v-icon large style="padding: 3px; color: #41916b; margin-right: 3px"
            >mdi-checkbox-marked-circle</v-icon
          >
          <span class="headline">{{ successTitle }}</span>
        </v-card-title>

        <v-card-text>{{ successMessage }}</v-card-text>

        <v-card-actions v-if="!resource.id">
          <v-spacer></v-spacer>
          <v-btn
            data-cy="btn-preencher-dados"
            color="#41916b"
            class="rounded-lg"
            variant="text"
            value="sim"
            style="color: white"
            @click="redirect()"
          >
            {{
              resource.kind_of_donor_id == 1
                ? $t('buttons.fill_glasgow_scale')
                : $t('buttons.fill_death_data')
            }}
          </v-btn>
          <v-btn
            data-cy="btn-nao"
            color="#8ead95"
            class="rounded-lg"
            variant="text"
            value="sim"
            @click="(dialog = false), $router.push({ path: '/pacientes' })"
            style="color: white"
          >
            {{ resource.kind_of_donor_id == 1 ? $t('buttons.no') : $t('buttons.cancel') }}
          </v-btn>
        </v-card-actions>
        <v-card-actions class="d-flex justify-end" v-else>
          <v-btn
            color="#41916b"
            variant="text"
            value="sim"
            class="rounded-lg"
            small
            @click="dialog = false"
            style="color: white"
          >
            {{ $t('buttons.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogPCR"
      transition="dialog-bottom-transition"
      max-width="900px"
      class="rounded-lg"
    >
      <v-card>
        <MortePCRForm :patientName="patientName" />
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogExam"
      persistent
      transition="dialog-bottom-transition"
      max-width="900px"
      class="rounded-lg"
    >
      <v-card>
        <ExamesEntradaCompVue
          :donorId="donorId"
          @fechar="(dialogExam = false), (dialog = true)"
        />
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="disclaimer"
      persistent
      transition="dialog-bottom-transition"
      max-width="400px"
      class="rounded-lg"
    >
      <v-card>
        <v-card-title>
          <v-icon left color="#41916b"> mdi-security </v-icon>
          <span class="text-h6 font-weight-light">{{$t('veracity_term.title')}}</span>
        </v-card-title>
        <v-card-text >
          <v-checkbox
            data-cy="checkbox-termo-veracidade"
            v-model="checkbox"
            color="#41916b"
            :label="$t('veracity_term.description')"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions class="d-flex justify-end" v-if="checkbox">
          <v-btn
            data-cy="botao-confirmar-termo"
            class="rounded-lg"
            small
            dark
            color="#41916b"
            @click="disclaimer = false"
          >
            {{$t('buttons.confirm')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="newKinship">
      <ParentescoForm />
    </v-dialog>

    <v-snackbar
      absolute
      background-color="white"
      color="#a11010"
      v-model="snackbar"
    >
      <v-icon color="white">mdi-alert-circle</v-icon>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "@/axios";
import { mapGetters, mapActions } from "vuex";
import MortePCRForm from "./mortePCR/MortePCRForm-comp.vue";
import ExamesEntradaCompVue from "./examesEntrada/CadastrarExamesEntrada-comp.vue";
import FormFieldsService from "@/service/FormFieldsService";
import ParentescoForm from "../../kinships/Kinship.vue";
import dateTimeLimit from "@/utils/dateTimeLimit"; 
import currentUser from "@/utils/currentUser";
import { profileSlug } from "@/helpers/profileSlug";
import Formatters from "@/utils/Formatters"; 
import {compareStr} from "@/utils/compareStrings";
export default {
  data() {
    return {
      identified: "",
      dialog: false,
      dialogPCR: false,
      hospital: [],
      sex: [],
      breeds: [],
      genders: [],
      opos: [],
      countries: [],
      diagnostics: [],
      kinships: [],
      state: [],
      inpatient_sectors: [],
      disableForm: false,
      donor_id: "",
      message: "",
      errorMessage: "",
      snackbar: false,
      donorForm: {}, 
      medicalRecordsRules: [
        (value) => !!value || "Campo obrigatório.",
        (value) => /^[0-9]+$/.test(value) || "Apenas números.",
      ],
      formRules: [(value) => !!value || "Campo obrigatório."],

      nameRules: [
        (value) => !!value || "Campo obrigatório.",
        (value) => !/^\d+$/.test(value) || "Apenas letras.",
      ],
      successTitle: "",
      patientName: "",
      dialogExam: false,
      donorId: "",
      checkbox: "",
      disclaimer: true,
      opo_reference: "",
      opc_reference: "",
      rgct: "",
      disableFlags: false,
      loading: false,
      cepBTN: false,
      hasCEP: false,
      openTextCity: false,
      cities: [],
      newKinship: false,
      diagnosis: [],
    };
  },

  components: {
    ParentescoForm,
    MortePCRForm,
    ExamesEntradaCompVue,
  },

  watch: {

    identified(value){
      if (value === "false") {
        this.resource.birth_date = null;
        this.resource.idade_provavel = null;
        return;
      }
      if(value === "true"){
          this.resource.idade_provavel = null;
      }
      },  
    },

  computed: {
    ...mapGetters({
      resource: "getPatientData",
    }),
 
    currentUser(){
      return currentUser.getUserData()
    },

    currentAbilities() {
      return this.currentUser.abilities;
    },

    profileSlug(){
      return profileSlug(this.currentUser.profile);
    },

    showAllUnities() {
      return this.currentUser.abilities?.includes('list_all_unities')
    },

    successMessage() {
      return this.resource.kind_of_donor_id == 1
        ? this.$t('messages.fill_glasgow_scale')
        : this.$t('messages.fill_death_data');
    },

    checkMEAndPCRPermissions() {
      return this.currentUser.abilities.some(
        (item) => item === "cad_donor_me" && item === "cad_donor_pcr"
      );
    },

    checkPCROrMEPermission() {
      return this.currentUser.abilities.some(
        (item) => item === "cad_donor_me" || item === "cad_donor_pcr"
      );
    },
 
    isAdmin() {
      return [
        'admin', 
        'diretor', 
        'plantonista_cet_coset', 
        'sesab_coset', 
        'tec_central'].includes(this.profileSlug);
    },

    isGroupDisabled() {
      return this.disableFlags && this.checkMEAndPCRPermissions;
    },

    isPCRDisabled() {
      const abilities = this.currentUser.abilities;
      return !abilities.includes("cad_donor_pcr");
    },

    isMEDisabled() {
      const abilities = this.currentUser.abilities;
      return !abilities.includes("cad_donor_me");
    },

    profile() { 
      return this.currentUser.profile;
    },

    authUser() {
      return this.$store.state;
    },

    maxDate() {
      const now = new Date()
      return now.toISOString().slice(0, 10)
    },

    otherOptions() { 
        return this.diagnosis.find(
          d => d.id === this.resource.initial_diagnosis_id
        ) 
    },

    canShowRGCT() {
      return this.identified == 'true' &&
        !!this.currentAbilities?.some(ability => compareStr(ability, 'insertrgct'))
    }, 
  },

  mounted() {  
    this.handleResourceState();
    this.getAllSelects();
    this.checkIfIdentified();
    this.handleProfileRules(this.authUser);
  },
 
  methods: {
    ...mapActions([
      "changeDonorId",
      "changePCRDeathData",
      "changeSnackBarData",
      "changePatientData",
    ]),

    async getAllSelects() {
      axios.get("/sex").then((response) => {
        this.sex = response.data.data;
      });
      axios.get("/breeds").then((response) => {
        this.breeds = response.data.data;
      });
      axios.get("/genders").then((response) => {
        this.genders = response.data.data;
      });
      axios.get("/kinship").then((response) => {
        this.kinships = response.data.data;
      });

      this.countries = await FormFieldsService.getCountries(); 
      this.state = await FormFieldsService.getStates();
      this.diagnosis = await FormFieldsService.getDiagnosis();

      axios.get("/unities").then((response) => {
        this.hospital = response.data.data;

        if (this.resource?.hospital_id) {
          this.changeCnes();
        }
      });
      axios.get("/inpatient_sectors").then((response) => {
        this.inpatient_sectors = response.data
          ? response.data
          : response.data.data;
      });
    },

    onlyDateInput(path,field, value) {  
       this[path][field] =
        dateTimeLimit.limiteInputOnlyDate(value)
    },

    handleResourceState() {
      const hasResourceId = !!this.resource.id;

      this.disclaimer = !hasResourceId;
      this.disableFlags = hasResourceId;

      if (!hasResourceId) {
        this.$set(this.resource, "country", "BRASIL");
      }
    },

    handleProfileRules(authUser) { 
      if (this.profileSlug == 'tec_central') { 
        this.rgct = this.resource.rgct;
      }
 
      if (!this.showAllUnities) {
        this.resource.hospital_id = Number(authUser.authUser.unity.id);
      }
 
      if (this.profileSlug == "plantonista_opo_edot") {
        this.resource.kind_of_donor_id = "1";
      }

      if (this.profileSlug == "plantonista_opc") {
        this.resource.kind_of_donor_id = "2";
        this.identified = "true";
      }
    },

    checkIfIdentified() {
      if (this.resource.name == null || this.resource.name == "") {
        this.identified = "";
      } else if (
        this.resource.name === "PACIENTE NÃO IDENTIFICADO" &&
        this.resource.action == "redirect"
      ) {
        this.identified = "true";
        this.changeName();
      } else if (
        this.resource.name === "PACIENTE NÃO IDENTIFICADO" &&
        this.resource.action == null
      ) {
        this.identified = "false";
      } else {
        this.identified = "true";
      }
    },

    changeCnes() {
      this.opo_reference = "";
      let cnes = this.hospital.find((x) => x.id == this.resource.hospital_id);

      this.resource.cnes = cnes.CNES;
      this.opo_reference = cnes.opo_reference;
      this.opc_reference = cnes.opc_reference;
    },

    handleKinshipChange(value) {
      if (value === "outros") {
        this.newKinship = true;

        this.selectedKinship = null;
      }
    },

    showAge() {
      if (!this.resource.birth_date) {
        this.resource.idade_provavel = null;
        return;
      }
       const birth_date = new Date(this.resource.birth_date);
       const today = new Date();

      let age = today.getFullYear() - birth_date.getFullYear();
      const monthDiff = today.getMonth() - birth_date.getMonth();

      if(
        monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth_date.getDate())
      ) {
        age--;
      }
      this.resource.idade_provavel = age;
    },
    redirect() {
      if (this.resource.kind_of_donor_id == 1) {
        this.$router.push({ path: "/escala-glasgow" });
      } else {
        this.dialog = false;
        this.dialogPCR = true;
      }
    },

    createDoador() {
      const isValid = this.$refs.form.validate();
       if(!isValid){ 
        this.changeSnackBarData({
            status: true,
            type: "erro",
            titleMessage: this.$t('messages.required_fields_title'),
            bodyMessage: this.$t('messages.required_fields_description'),
            cardColor: "red",
            titleIcon: "mdi-close-circle",
          });

          setTimeout(() => this.closeSnackBar(), 5000);
        }else{
          this.loading = true;

        this.identified == "false"
          ? ((this.resource.name = "PACIENTE NÃO IDENTIFICADO"),
            (this.resource.not_suitable_for_donor_flg = 1))
          : (this.resource.name,
            (this.resource.not_suitable_for_donor_flg = 0));

        this.resource.hospital_id
          ? (this.resource.unity_id = this.resource.hospital_id)
          : (this.resource.unity_id = this.currentUser.unity_id);

        this.resource.donor_status_id = this.identified == "true" ? 1 : 15;
        this.resource.user_id = this.resource.user_id
          ? this.resource.user_id
          : this.currentUser.id;

        this.resource.name = this.resource.name.toUpperCase();
        this.resource.cpf = Formatters.removeNonNumeric(this.resource.cpf) 
        
        const doador = { ...this.resource, rgct: this.resource.rgct ? this.resource.rgct.replace(/-/g, "") : this.resource.rgct };
        const method = this.resource.id ? "PATCH" : "POST";
        const url = this.resource.id
          ? `/donor/${this.resource.id}`
          : `/donor/salvarDoador`;

        axios({
          method: method,
          url: url,
          data: doador,
        })
          .then((response) => {
            this.loading = false;
            this.changePCRDeathData({});
            if (method == "PATCH") {
              this.disableForm = false;
              this.successTitle = "Cadastro atualizado com sucesso!";
              this.changeSnackBarData({
                status: true,
                type: "success",
                titleMessage: this.successTitle,
                bodyMessage: "",
                cardColor: "success",
                titleIcon: "mdi-check-circle",
              });

              setTimeout(() => {
                this.closeSnackBar();
                if (this.resource.action == "redirect") {
                  this.$router.push("/escala-glasgow");
                } else {
                  this.$router.push("/pacientes");
                }
              }, 3000);
            } else {
              this.donorId = response.data.id.id;
              this.changeDonorId(this.donorId);
              this.disableForm = true;
              this.successTitle = "Cadastro realizado com sucesso!";

              if (this.resource.kind_of_donor_id == 2) {
                this.dialog = true;
              } else {
                this.dialogExam = true;
              }
            }
          })
          .catch((err) => {
            this.loading = false;
           
            this.changeSnackBarData({
              status: true,
              type: "erro",
              titleMessage: "Algo deu errado!",
              bodyMessage: Formatters.formatErrorMessage(err),
              cardColor: "red",
              titleIcon: "mdi-close-circle",
            });

            setTimeout(() => {
              this.closeSnackBar();
            }, 5000);
          });
      }
    },

    addRGCT() {
      if (this.rgct) {
        let data = new Object();
        data.rgct = this.rgct.replace(/-/g, "");

        axios
          .patch(`/donor/insert_rgct/${this.resource.id}`, data)
          .then((response) => {
            this.changeSnackBarData({
              status: true,
              type: "success",
              titleMessage: response.data.message,
              bodyMessage: "",
              cardColor: "#41916b",
              titleIcon: "mdi-checkbox-marked-circle",
            });

            setTimeout(() => {
              this.closeSnackBar();
              this.$router.push("/pacientes");
            }, 3000);
          })
          .catch((err) => {
            this.changeSnackBarData({
              status: true,
              type: "erro",
              titleMessage: "Algo deu errado!",
              bodyMessage: Formatters.formatErrorMessage(err),
              cardColor: "red",
              titleIcon: "mdi-close-circle",
            });

            setTimeout(() => {
              this.closeSnackBar();
            }, 5000);
          });
      }
    },

    async getCities() {
      if (this.resource.state != "Bahia") {
        this.openTextCity = true;
      } else {
        this.openTextCity = false;
        this.cities = await FormFieldsService.getMunicipalities(
          this.resource.state
        );
      }
    },

    async getAddress() {
      this.cepBTN = true;
      const data = await FormFieldsService.getCep(this.resource.zip_code);
 
      if (!data.error) {
        if (data.estado == "Bahia") {
          this.openTextCity = false;
          this.cities = await FormFieldsService.getMunicipalities(data.estado);
        } else {
          this.openTextCity = true;
        }

        this.$set(this.resource, "municipality", data.localidade.toUpperCase());
        this.$set(this.resource, "district", data.bairro.toUpperCase());
        this.$set(this.resource, "address", data.logradouro.toUpperCase());
        this.$set(this.resource, "state", data.estado);
      }else{
        this.$set(this.resource, "municipality", "");
        this.$set(this.resource, "district", "");
        this.$set(this.resource, "address", "");
        this.$set(this.resource, "state", "");

        this.changeSnackBarData({
          status: true,
          type: "erro",
          titleMessage: "CEP não encontrado!",
          bodyMessage: "",
          cardColor: "red",
          titleIcon: "mdi-close-circle",
        });
      }

      setTimeout(() => {
        this.cepBTN = false;
        this.closeSnackBar();
      }, 2000);
    },

    changeName() {
      this.resource.name === "PACIENTE NÃO IDENTIFICADO"
        ? (this.resource.name = "")
        : this.resource.name;
      this.$refs.form.resetValidation();
    },

    closeSnackBar() {
      this.changeSnackBarData({
        status: false,
        type: "",
        titleMessage: "",
        bodyMessage: "",
        cardColor: "",
        titleIcon: "",
      });
    },
  },
};
</script>
